// What a sync response MEANS for a queued desk event (plan "Offline" sync
// outcomes; Task 12 notes, "For Task 14").
//
//   done       — the server decided and the event stands: `applied` (silent)
//                or `already_checked_in` (informational — the normal
//                double-scan case, not an error).
//   attention  — a final answer that needs a human: `superseded`/`rejected`
//                (409), not found (404), an invalid request (400), or an
//                auth problem (401/403: session expired, membership revoked).
//                Goes to the Needs-attention tray with the original event.
//                NEVER retried automatically, NEVER dropped.
//   retry      — nothing was decided: no network, 429 rate limit, 5xx.
//                Paused/retried by TanStack Query; parked in the tray after
//                8 attempts.
//
// Only a 200/409 carrying `outcome` is a server decision about the event
// itself. A 401/403 is about the caller and is never recorded as `rejected`.

export type DoneOutcome = 'applied' | 'already_checked_in';

export interface RawResponse {
  /** HTTP status, or 0 when the request never got a response (offline,
   * DNS, TLS, CORS, connection reset). */
  status: number;
  body: Record<string, unknown>;
}

export type AttentionCategory = 'decision' | 'not_found' | 'invalid' | 'auth';
export type RetryReason = 'network' | 'rate_limited' | 'server_error';

export type Classified =
  | { kind: 'done'; outcome: DoneOutcome; body: Record<string, unknown> }
  | { kind: 'attention'; category: AttentionCategory; reason: string; message: string; status: number }
  | { kind: 'retry'; reason: RetryReason; status: number };

const DEFAULT_MESSAGES: Record<AttentionCategory, string> = {
  decision: 'The server refused this event.',
  not_found: 'No booking with this code was found for your business.',
  invalid: 'The server could not read this event.',
  auth: 'Your session expired or your access to this property changed — sign in again, then retry.',
};

function stringField(body: Record<string, unknown>, key: string): string | null {
  const value = body[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

export function classifyResponse(response: RawResponse): Classified {
  const { status, body } = response;
  const outcome = stringField(body, 'outcome');
  const reason = stringField(body, 'reason');
  const error = stringField(body, 'error');

  if (status === 0) return { kind: 'retry', reason: 'network', status };
  if (status === 429) return { kind: 'retry', reason: 'rate_limited', status };
  if (status === 408 || status >= 500) return { kind: 'retry', reason: 'server_error', status };

  if (status === 401 || status === 403) {
    return {
      kind: 'attention',
      category: 'auth',
      reason: error === 'desk_key_revoked' ? 'desk_key_revoked' : status === 401 ? 'session_expired' : 'forbidden',
      message: DEFAULT_MESSAGES.auth,
      status,
    };
  }

  if (status >= 200 && status < 300) {
    if (outcome === 'applied' || outcome === 'already_checked_in') return { kind: 'done', outcome, body };
    // A 2xx without a known outcome is not something the desk can trust as
    // "done" — surface it rather than assume.
    return {
      kind: 'attention',
      category: 'invalid',
      reason: outcome ?? 'unexpected_response',
      message: 'The server answered in a way the desk did not expect.',
      status,
    };
  }

  if (status === 409 && (outcome === 'superseded' || outcome === 'rejected')) {
    return {
      kind: 'attention',
      category: 'decision',
      reason: reason ?? outcome,
      message: error ?? DEFAULT_MESSAGES.decision,
      status,
    };
  }

  if (status === 404) {
    return { kind: 'attention', category: 'not_found', reason: reason ?? 'not_found', message: error ?? DEFAULT_MESSAGES.not_found, status };
  }

  return {
    kind: 'attention',
    category: 'invalid',
    reason: reason ?? `http_${status}`,
    message: error ?? DEFAULT_MESSAGES.invalid,
    status,
  };
}

/** Thrown by a desk mutationFn for anything that is not `done`, so TanStack
 * Query's retry policy can look at `classified.kind`. */
export class DeskSyncError extends Error {
  readonly classified: Exclude<Classified, { kind: 'done' }>;

  constructor(classified: Exclude<Classified, { kind: 'done' }>) {
    super(classified.kind === 'attention' ? classified.message : `sync paused: ${classified.reason}`);
    this.name = 'DeskSyncError';
    this.classified = classified;
  }
}

/** Total attempts (first try + retries) before a retryable event is parked
 * in the Needs-attention tray (plan "Offline": "parked after 8 attempts"). */
export const MAX_SYNC_ATTEMPTS = 8;

/** TanStack Query `retry` callback. `failureCount` is 0 on the first
 * failure, so `< MAX_SYNC_ATTEMPTS - 1` allows exactly 8 attempts. Only a
 * `retry` classification is ever retried. */
export function shouldRetrySync(failureCount: number, error: unknown): boolean {
  if (!(error instanceof DeskSyncError)) return false;
  return error.classified.kind === 'retry' && failureCount < MAX_SYNC_ATTEMPTS - 1;
}

/** Back-off between attempts: 2 s, 4 s, … capped at 60 s; a 429 waits at
 * least 30 s (the server's window is one minute). */
export function syncRetryDelay(failureCount: number, error: unknown): number {
  const base = Math.min(2000 * 2 ** failureCount, 60_000);
  if (error instanceof DeskSyncError && error.classified.kind === 'retry' && error.classified.reason === 'rate_limited') {
    return Math.max(base, 30_000);
  }
  return base;
}
